/**
 * Reading a refused stake
 *
 * A LimitEvents row is written by the casino wallet every time a bet is turned
 * away for hitting a limit. Each row already carries its own prose — headline,
 * explanation, decisionPath — so this file does not re-explain the refusal. It
 * covers the two things prose cannot do:
 *
 *   1. the vocabulary the history filters and groups on, in words an admin can
 *      act on ("you can change this here" vs "this needs a code deploy");
 *   2. turning an event back into the limit form on the Limits page, so a
 *      refusal is one tap away from the rule that caused it.
 */

import {
  ExclamationTriangleIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  NoSymbolIcon,
  TrophyIcon,
  PuzzlePieceIcon,
  CubeIcon,
  Square3Stack3DIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

import { GAME_PROVIDERS, getAllSportsEvents } from './limitScopes';
import { getGameNameWithFallback } from './gameNameMapping';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Why the bet was refused. `limit.breachType` on the event. */
export const BREACH_TYPES = {
  'above-maximum': {
    label: 'Over the maximum',
    short: 'Too big',
    Icon: ExclamationTriangleIcon,
    chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    accent: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800'
  },
  'below-minimum': {
    label: 'Under the minimum',
    short: 'Too small',
    Icon: ArrowTrendingDownIcon,
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    accent: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800'
  },
  'match-total-exceeded': {
    label: 'Too much on one match',
    short: 'Match total',
    Icon: ChartBarIcon,
    chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    accent: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800'
  },
  'forbidden-amount': {
    label: 'Blocked amount',
    short: 'Deny-list',
    Icon: NoSymbolIcon,
    chip: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    accent: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-300 dark:border-slate-600'
  }
};

/**
 * Where the limit came from. `configurable` is the line that matters: it splits
 * "an admin can fix this in this panel" from "this needs a code deploy".
 */
export const LIMIT_SOURCES = {
  'limits-table': {
    label: 'Set by an operator',
    configurable: true,
    summary: 'A limit configured in this panel and inherited down the hierarchy. The closest account holding a matching limit decides.'
  },
  'match-exposure': {
    label: 'Running total on a match',
    configurable: false,
    summary: 'The per-ticket cap re-used as a cap on everything the player has already staked on the same fixture. Derived from a configured limit rather than set separately.'
  },
  'provider-hardcoded': {
    label: 'Provider cap in code',
    configurable: false,
    summary: 'A ceiling hardcoded in the integration for one game provider, applied on top of any configured limit.'
  },
  'game-hardcoded': {
    label: 'Game cap in code',
    configurable: false,
    summary: 'A ceiling hardcoded for a game category. Only consulted when the hierarchy had no matching limit.'
  },
  'event-tier-hardcoded': {
    label: 'Event cap in code',
    configurable: false,
    summary: 'A ceiling hardcoded for a class of sporting event. Only consulted when the hierarchy had no sports limit.'
  },
  'global-fallback': {
    label: 'Last-resort cap',
    configurable: false,
    summary: 'The catch-all ceiling used when nothing else produced a limit for this bet.'
  },
  'player-hardcoded': {
    label: 'Player cap in code',
    configurable: false,
    summary: 'A ceiling hardcoded for one named account, overriding the general rule.'
  },
  'forbidden-amount': {
    label: 'Deny-listed amount',
    configurable: false,
    summary: 'An exact stake the provider cannot process. Not a ceiling — smaller and larger stakes both go through.'
  }
};

/**
 * What the player sees, per integration. `transaction.provider` on the event.
 * `short` is what fits on a list row next to the numbers.
 */
export const EVENT_PROVIDERS = {
  pm8: { label: 'PM8 / Eva', short: 'PM8', product: 'casino' },
  databetrix: { label: 'Databetrix', short: 'Databetrix', product: 'casino' },
  betinovio: { label: 'Betinovio', short: 'Betinovio', product: 'casino' },
  rebeltech: { label: 'RebelTech', short: 'RebelTech', product: 'casino' },
  gr8: { label: 'Classic Sport', short: 'Classic', product: 'sports' },
  betconstruct: { label: 'Premium Sport', short: 'Premium', product: 'sports' },
  turbostars: { label: 'TurboStars', short: 'TurboStars', product: 'sports' }
};

export const PRODUCTS = {
  casino: {
    label: 'Casino',
    Icon: PuzzlePieceIcon,
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
  },
  sports: {
    label: 'Sports',
    Icon: TrophyIcon,
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  }
};

export const getBreachMeta = (event) =>
  BREACH_TYPES[event?.limit?.breachType] || BREACH_TYPES['above-maximum'];

export const getSourceMeta = (event) =>
  LIMIT_SOURCES[event?.limit?.limitSource] || null;

export const getProviderLabel = (event) => {
  const provider = event?.transaction?.provider;
  return EVENT_PROVIDERS[provider]?.label || event?.transaction?.providerLabel || provider || 'Unknown';
};

/**
 * True when the refusal traces back to a row an admin can open and change.
 * The event carries this itself; the owner check is what makes it actionable,
 * because a configurable limit with no owner has nowhere to be edited.
 */
export const isFixableInPanel = (event) =>
  event?.limit?.operatorConfigurable === true && Boolean(event?.limit?.ownerUsername);

// ---------------------------------------------------------------------------
// What got limited
// ---------------------------------------------------------------------------

// Same four buckets, and the same colours, the Limits tab groups rules into. A
// league blocked here should look like the league blocked over there.
const SCOPE_KINDS = {
  universal: { Icon: ExclamationCircleIcon, color: 'text-red-600 dark:text-red-400' },
  sports: { Icon: TrophyIcon, color: 'text-emerald-600 dark:text-emerald-400' },
  provider: { Icon: Square3Stack3DIcon, color: 'text-purple-600 dark:text-purple-400' },
  game: { Icon: CubeIcon, color: 'text-blue-600 dark:text-blue-400' }
};

/** Identifiers that stand for a whole family of table games rather than one. */
const GENERAL_GAME_CATEGORIES = {
  blackjack: 'Blackjack (all variants)',
  roulette: 'Roulette (all variants)',
  baccarat: 'Baccarat (all variants)'
};

/**
 * The league, game, provider or category the refusal was about — the one thing
 * a list row is scanned for, and the thing an admin would go and change.
 *
 * Read off the limit that did the refusing wherever it names one. Caps that
 * live in code carry no identifier, so those fall back to the ticket, which
 * still says which competition or game the stake was on.
 *
 * @returns {{key: string, kind: string, emoji: string|null, Icon: Function,
 *            color: string, label: string, detail: string|null}}
 */
export const describeEventScope = (event, customSportsEvents = []) => {
  const limit = event?.limit || {};
  const transaction = event?.transaction || {};
  const identifier = String(limit.identifier || '');
  const sportsBet = transaction.sportsBet;

  const build = (kind, label, extra = {}) => ({
    kind,
    label,
    emoji: null,
    detail: null,
    ...SCOPE_KINDS[kind],
    ...extra,
    key: extra.key || `${kind}:${label}`
  });

  const findSportsEvent = (slug) =>
    slug ? getAllSportsEvents(customSportsEvents).find((entry) => entry.id === slug) : null;

  if (limit.limitType === 'universal' || identifier === 'all') {
    return build('universal', 'All games', { kindLabel: 'Everything', key: 'universal:all' });
  }

  if (transaction.product === 'sports') {
    // The concrete fixture, kept as the second line: it is what the player was
    // actually on, which the scope alone does not say.
    const match = sportsBet?.matches?.[0]?.matchLabel || event?.exposure?.matchLabel || null;

    if (limit.limitType === 'sports') {
      return build('sports', 'All sports', {
        emoji: '🏆',
        kindLabel: 'All sports',
        detail: match,
        key: 'sports:all'
      });
    }

    if (limit.limitType === 'sports-event') {
      const slug = identifier.replace(/^sports:/, '');
      const known = findSportsEvent(slug);
      return build('sports', limit.matchedLimitRecord?.sportsEventName || known?.name || slug, {
        emoji: known?.icon || '🏆',
        kindLabel: 'Sports event',
        detail: match,
        key: `sports:${slug}`
      });
    }

    // A cap from code: name the competition the ticket was on instead.
    const slug = sportsBet?.detectedEventSlugs?.[0];
    const known = findSportsEvent(slug);
    const competition = sportsBet?.competitions?.[0] || known?.name;
    return build('sports', competition || 'Sports', {
      emoji: known?.icon || '🏆',
      kindLabel: 'Sports',
      detail: match,
      key: `sports:${slug || competition || 'any'}`
    });
  }

  if (limit.limitType === 'provider') {
    const provider = GAME_PROVIDERS.find((entry) => entry.id === identifier);
    return build('provider', provider?.name || identifier, {
      kindLabel: 'Provider',
      detail: transaction.gameName || null,
      key: `provider:${identifier}`
    });
  }

  // A general limit covers a whole table-game family, so the family is what got
  // limited — naming the one variant that happened to trip it would be wrong.
  const generalCategory = GENERAL_GAME_CATEGORIES[identifier];
  if (generalCategory) {
    return build('game', generalCategory, {
      kindLabel: 'Game category',
      detail: transaction.gameName || null,
      key: `game:${identifier}`
    });
  }

  const gameId = identifier || transaction.gameId;
  const gameName =
    (identifier && getGameNameWithFallback(identifier, '')) ||
    transaction.gameName ||
    transaction.gameCategory ||
    gameId;

  return build('game', gameName || 'Casino', {
    kindLabel: 'Game',
    // Only worth a second line when it says something the label does not.
    detail: transaction.gameName && transaction.gameName !== gameName ? transaction.gameName : null,
    key: `game:${gameId || gameName || 'any'}`
  });
};

/**
 * What the player was actually on — a game, a match, a bet — as opposed to
 * the limit that refused it. Repeated refusals of the same ticket stack on
 * this key so a day of Sweet Bonanza retries is one row, not twenty.
 */
export const describeEventTarget = (event, scope) => {
  const transaction = event?.transaction || {};
  const sportsBet = transaction.sportsBet || {};
  const match =
    sportsBet.matches?.[0]?.matchLabel ||
    event?.exposure?.matchLabel ||
    null;
  const gameId = transaction.gameId || null;
  const gameName = transaction.gameName || null;
  const betId =
    sportsBet.betId ||
    transaction.betId ||
    event?.exposure?.betId ||
    null;

  if (match) {
    return { key: `match:${match}`, label: match, kind: 'match' };
  }

  if (betId) {
    return { key: `bet:${betId}`, label: String(betId), kind: 'bet' };
  }

  if (gameId) {
    return {
      key: `game:${gameId}`,
      label: gameName || scope?.label || gameId,
      kind: 'game'
    };
  }

  if (gameName) {
    return {
      key: `gameName:${gameName.trim().toLowerCase()}`,
      label: gameName,
      kind: 'game'
    };
  }

  // Nothing names the ticket — keep the row alone rather than stacking every
  // "all games" refusal into one pile just because the limit looks the same.
  return {
    key: `event:${event?.eventId || 'unknown'}`,
    label: scope?.label || 'Unknown',
    kind: scope?.kind || 'game'
  };
};

const clusterIdentity = (event, scope, grouping) => {
  const target = describeEventTarget(event, scope);
  const player = event?.player?.username || event?.username || 'unknown';

  // The outer cut is already the game or league, so stacking on the same
  // target would wrap the whole group in one extra header. Use the next
  // useful axis: the fixture/variant if there is one, otherwise the player.
  if (grouping === 'scope') {
    if (scope?.detail) {
      return { key: `detail:${scope.detail}`, label: scope.detail, kind: 'detail', axis: 'detail' };
    }
    return { key: `player:${player}`, label: `@${player}`, kind: 'player', axis: 'player' };
  }

  return { ...target, axis: 'target' };
};

const bucketByIdentity = (events, identityFor) => {
  const buckets = new Map();

  events.forEach((event) => {
    const identity = identityFor(event);
    if (!buckets.has(identity.key)) {
      buckets.set(identity.key, { ...identity, events: [] });
    }
    buckets.get(identity.key).events.push(event);
  });

  return Array.from(buckets.values());
};

/**
 * Cut a list of refusals into singles and stacks of the same game / match /
 * player. `grouping` is the outer cut, so the inner one uses a different axis
 * — opening "Sweet Bonanza" should not reveal one Sweet Bonanza stack.
 */
export const clusterEventsByTarget = (events, getScope, grouping = 'time') => {
  const identityFor = (event) => clusterIdentity(event, getScope?.(event) || null, grouping);
  let buckets = bucketByIdentity(events, identityFor);

  // A league group whose every refusal is the same fixture still looks like
  // one stack. If several players hit it, cut by player instead.
  if (
    grouping === 'scope' &&
    buckets.length === 1 &&
    buckets[0].axis !== 'player' &&
    buckets[0].events.length > 1
  ) {
    const players = new Set(events.map((event) => event.player?.username || event.username).filter(Boolean));
    if (players.size > 1) {
      buckets = bucketByIdentity(events, (event) => {
        const player = event?.player?.username || event?.username || 'unknown';
        return { key: `player:${player}`, label: `@${player}`, kind: 'player', axis: 'player' };
      });
    }
  }

  return buckets
    .sort((a, b) => (Number(b.events[0]?.occurredAtMs) || 0) - (Number(a.events[0]?.occurredAtMs) || 0))
    .map((bucket) =>
      bucket.events.length === 1
        ? { type: 'single', event: bucket.events[0] }
        : { type: 'cluster', ...bucket }
    );
};

/** Players, amount span and time span for a stack header. */
export const summarizeCluster = (events) => {
  const players = [];
  const seenPlayers = new Set();
  const tried = [];
  const caps = [];
  let currency = null;
  let mixedCurrency = false;
  let owner = events[0]?.limitOwner ?? null;
  let mixedOwner = false;

  events.forEach((event) => {
    const username = event.player?.username || event.username;
    if (username && !seenPlayers.has(username)) {
      seenPlayers.add(username);
      players.push(username);
    }

    const amounts = describeAmounts(event);
    if (amounts.tried !== null) tried.push(amounts.tried);
    if (amounts.cap !== null) caps.push(amounts.cap);

    if (currency == null) currency = amounts.currency;
    else if (amounts.currency && amounts.currency !== currency) mixedCurrency = true;

    if ((event.limitOwner ?? null) !== owner) mixedOwner = true;
  });

  const times = events.map((event) => Number(event.occurredAtMs || 0)).filter(Boolean);

  return {
    players,
    playerCount: players.length,
    triedMin: tried.length ? Math.min(...tried) : null,
    triedMax: tried.length ? Math.max(...tried) : null,
    capMin: caps.length ? Math.min(...caps) : null,
    capMax: caps.length ? Math.max(...caps) : null,
    currency: mixedCurrency ? null : currency,
    owner: mixedOwner ? null : owner,
    oldestMs: times.length ? Math.min(...times) : null,
    newestMs: times.length ? Math.max(...times) : null,
    count: events.length
  };
};

/**
 * The two figures whose comparison refused the bet, and the gap between them.
 *
 * Which figure is which changes with the breach: a match-total refusal compares
 * a running total rather than the stake, and a below-minimum one is measured
 * against a floor rather than a ceiling. Callers get flags rather than wording
 * so the list and the detail sheet can label them at their own length.
 */
export const describeAmounts = (event) => {
  const limit = event?.limit || {};
  const asNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

  return {
    currency: event?.transaction?.currency || 'USD',
    tried: asNumber(limit.evaluatedAmount),
    cap: asNumber(limit.evaluatedAgainst),
    gap: asNumber(limit.exceededBy),
    gapPct: asNumber(limit.exceededByPct),
    isTooSmall: limit.breachType === 'below-minimum',
    isRunningTotal: limit.breachType === 'match-total-exceeded'
  };
};

// ---------------------------------------------------------------------------
// Turning a refusal back into something the limit editor understands
//
// LimitScopeModal already knows how to attach a limit to any account in a
// player's chain, in any currency, for a game / provider / league. It takes the
// bet it was opened from and works the scope out itself, so a refusal only has
// to be described in the same terms a bet is.
// ---------------------------------------------------------------------------

/**
 * The refused bet, shaped like the bets LimitScopeModal is normally opened
 * from.
 *
 * The scope is read off the limit that actually did the refusing wherever
 * possible, because that is the rule an admin came to change. Caps that live in
 * code carry no identifier, so those fall back to what the ticket says — which
 * is the scope a new limit would have to cover.
 */
export const buildBetFromEvent = (event) => {
  const transaction = event?.transaction || {};
  const limit = event?.limit || {};
  const identifier = String(limit.identifier || '');

  if (transaction.product === 'sports') {
    const fromLimit =
      limit.limitType === 'sports-event'
        ? limit.matchedLimitRecord?.sportsEventName || identifier.replace(/^sports:/, '')
        : null;

    const fromTicket =
      transaction.sportsBet?.competitions?.[0] ||
      transaction.sportsBet?.detectedEventSlugs?.[0] ||
      null;

    return {
      isSports: true,
      // A limitType of 'sports' covers every sport, so naming a league here
      // would offer a narrower scope than the one that refused the bet.
      leagueName: limit.limitType === 'sports' ? null : fromLimit || fromTicket
    };
  }

  // A cap on a whole table-game family names the family, not the variant the
  // player happened to be on, so both have to reach the editor: the family so
  // it can be preselected, the variant so it can still be chosen instead.
  const family =
    GENERAL_GAME_CATEGORIES[identifier] ? identifier : null;

  return {
    isSports: false,
    gameCode: transaction.gameId || (limit.limitType === 'game' && !family ? identifier : null),
    gameName: transaction.gameName,
    generalGameId: family,
    providerLabel:
      transaction.gameProvider || (limit.limitType === 'provider' ? identifier : null)
  };
};

/**
 * Which of the editor's scope options should start selected — the scope that
 * actually refused the bet. Keys have to match the ones buildBetScopeOptions
 * produces, or nothing matches and it falls back to the narrowest option.
 *
 * Returns null when the cap lives in code and names no scope; there is no
 * "right" answer then, so the narrowest option is as good a start as any.
 */
export const getPreferredScopeKey = (event) => {
  const limit = event?.limit || {};
  const identifier = String(limit.identifier || '');

  switch (limit.limitType) {
    case 'universal':
      return 'universal';
    case 'sports':
      return 'sports:all';
    case 'sports-event':
      return `sports:${identifier.replace(/^sports:/, '')}`;
    case 'provider':
      return `provider:${identifier}`;
    case 'game':
    case 'game-general':
      return identifier ? `game:${identifier}` : null;
    default:
      return identifier === 'all' ? 'universal' : null;
  }
};

/**
 * A maximum that would have let this exact bet through, for the one-tap button
 * in the limit editor. Only meaningful for a ceiling that was crossed: a stake
 * refused for being too small or for being deny-listed is not fixed by raising
 * anything, so those get no suggestion.
 */
export const buildLimitSuggestion = (event) => {
  const limit = event?.limit || {};
  const currency = event?.transaction?.currency;
  const amount = Number(limit.evaluatedAmount);

  const raisable = ['above-maximum', 'match-total-exceeded'].includes(limit.breachType);
  if (!raisable || !currency || !Number.isFinite(amount) || amount <= 0) return null;

  return { currency, maxBet: amount };
};

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/** "just now", "12m ago", "3h ago", "2d ago" — enough to place an event. */
export const timeAgo = (occurredAtMs, now = Date.now()) => {
  const elapsed = Math.max(0, now - Number(occurredAtMs || 0));
  const minutes = Math.floor(elapsed / 60000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

export const formatClock = (occurredAtMs) =>
  new Date(Number(occurredAtMs || 0)).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });

export const formatDateTime = (occurredAtMs) =>
  new Date(Number(occurredAtMs || 0)).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium'
  });

/** "Today", "Yesterday", or a written date — the heading over a day's refusals. */
export const formatDayHeading = (dateBucket) => {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  if (dateBucket === today) return 'Today';
  if (dateBucket === yesterday) return 'Yesterday';

  return new Date(`${dateBucket}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};
